import React, { FC, useEffect, useRef, useState, useMemo } from "react";

import { SubscriptionAdvantagesList } from "../../../SubscriptionAdvantagesList/SubscriptionAdvantagesList";

// UI components
import { Card } from "@UI/Components/Card/Card";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Icon } from "@UI/Components/Icon/Icon";
import { Box } from "@UI/Components/Box/Box";
import { BrandPlus } from "@UI/Components/Icons/BrandPlus";
import { ResponsiveFormattedMessage } from "@Shared/Components/ResponsiveFormattedMessage/ResponsiveFormattedMessage";

// i18n
import {
  FormattedMessage,
  MessageDescriptor,
} from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

// hooks
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { useLocation } from "@Libs/Router/hooks/useLocation";

// types
import { RouterLinkProps } from "@Libs/Router/Components/RouterLink/RouterLink";
import { useCurrentBasket } from "@modules/Purchasing/hooks/useCurrentBasket";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { useDataLayer } from "@Libs/DataLayer/hooks/useDataLayer";
import { IErrorResponse } from "@api/createApi";
import { isSubscriptionProduct } from "@modules/Subscriptions/services/subscriptionPlans";
import { v4 as uuidv4 } from "uuid";
import { LabeledSwitch } from "@UI/Components/LabeledSwitch/LabeledSwitch";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import useI18n from "@I18n/hooks/useI18n";
import { useSubscriptionPlans } from "@modules/Subscriptions/hooks/useSubscriptionPlans";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { IUnsavedBasket } from "@modules/Purchasing/services/baskets";
import { Checkout } from "@modules/Purchasing/Flows/Checkout/Checkout";
import { mergeNavigateProps } from "@Libs/Router/Components/Navigate/Navigate";
import { PrettyRate } from "@Shared/Components/PrettyRate/PrettyRate";
import useThemeToken from "@UI/hooks/useThemeToken";

interface Props {
  link: RouterLinkProps;
}

type ISubscriptionPlanName = "quarterly_subscription" | "yearly_subscription";

const PLAN_LABELS: {
  [key in ISubscriptionPlanName]: MessageDescriptor;
} = {
  quarterly_subscription: messages.quarterlyLabel,
  yearly_subscription: messages.yearlyLabel,
};

export const ChooseFormulaPageRedesignSubscriptionFormulaCard: FC<Props> = ({
  link,
}) => {
  const element = useRef<HTMLDivElement>(null);
  const { hash } = useLocation();
  const { isMobile } = useMediaQuery();
  const hashMatch = useMemo(
    () => hash === "#subscription-formula-card",
    [hash]
  );
  const { formatMessage } = useI18n();
  const pink500 = useThemeToken("colors", "pink.500");
  const pink600 = useThemeToken("colors", "pink.600");
  const orange500 = useThemeToken("colors", "orange.500");
  const orange600 = useThemeToken("colors", "orange.600");

  const { subscriptionPlans, isLoading: isSubscriptionPlansLoading } =
    useSubscriptionPlans();
  const dataLayer = useDataLayer();
  const [selectedPlanName, setSelectedPlanName] =
    useState<ISubscriptionPlanName>("quarterly_subscription");
  const navigate = useNavigate();

  const {
    saveBasket,
    isLoading: isCurrentBasketLoading,
    currentBasket,
    currentBasketId,
    isError,
  } = useCurrentBasket();
  const paramsBasketId = useSearchParams("resourceId");

  const isBasketFromParamsLoading = !!(
    paramsBasketId && currentBasketId !== paramsBasketId
  );

  useEffect(() => {
    if (!isCurrentBasketLoading && currentBasket?.items) {
      const subscriptionItem = currentBasket.items.find(({ item }) =>
        isSubscriptionProduct(item)
      );
      if (isSubscriptionProduct(subscriptionItem?.item)) {
        setSelectedPlanName(
          subscriptionItem.item.name as ISubscriptionPlanName
        );
      }
    }
  }, [isCurrentBasketLoading]);

  const handleLockedBasket = ({ items }) => {
    dataLayer.setQueryData("currentBasketId", null);
    const id = uuidv4();
    saveBasket(
      {
        id,
        items,
      },
      {
        onSuccess: (basket) => {
          dataLayer.setQueryData("currentBasketId", id);
          dataLayer.setQueryData("currentBasket", basket);
        },
      }
    );
  };

  useEffect(() => {
    if (
      ((!paramsBasketId && !isError) ||
        (paramsBasketId && !isBasketFromParamsLoading)) &&
      currentBasket?.items?.find(({ item }) => isSubscriptionProduct(item))
    ) {
      const nonSubscriptionItems = currentBasket.items.filter(
        (basketItem) => basketItem.itemType !== "product"
      );
      saveBasket(
        {
          id: currentBasket.id,
          items: nonSubscriptionItems,
        },
        {
          onError: (error: IErrorResponse) => {
            if (error.response?.status === 423) {
              handleLockedBasket({ items: nonSubscriptionItems });
            }
          },
        }
      );
    }
  }, []);

  useEffect(() => {
    if (hashMatch && element.current && isMobile) {
      setTimeout(() => {
        element.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
    }
  }, [hashMatch, element.current, isMobile]);

  const selectedPlan =
    subscriptionPlans &&
    subscriptionPlans.find((plan) => plan.name === selectedPlanName);

  if (isCurrentBasketLoading || isSubscriptionPlansLoading) {
    return <LoadingScreen />;
  }

  const getSubscriptionLabel = (plan: ISubscriptionPlanName) => {
    return formatMessage(PLAN_LABELS[plan]);
  };

  const handleSelectPlan = (plan: ISubscriptionPlanName) => {
    setSelectedPlanName(plan);
  };

  const handleContinue = () => {
    const nonSubscriptionItems = currentBasket.items.filter(
      (basketItem) => basketItem.itemType !== "product"
    );

    const items: IUnsavedBasket["items"] = [
      ...nonSubscriptionItems.map(({ itemId, itemType, quantity }) => ({
        itemId,
        itemType,
        quantity,
      })),
      {
        itemId: selectedPlan.id,
        itemType: "product",
        quantity: 1,
      },
    ];

    saveBasket(
      {
        id: currentBasket.id,
        items,
      },
      {
        onSuccess: () => {
          const navigateProps = mergeNavigateProps(
            {
              to: link.route,
              params: link.params,
              queryParams: link.query,
            },
            {
              to: Checkout.root,
            }
          );
          navigate(navigateProps.to, navigateProps);
        },
        onError: async (error: IErrorResponse) => {
          if (error.response?.status === 423) {
            handleLockedBasket({ items });
          } else {
            navigate(["SERVER_ERROR"]);
          }
        },
      }
    );
  };

  return (
    <Card
      id="subscription-formula-card"
      data-testid="subscription-formula-card"
      position="relative"
      ref={element}
      header={
        <Box
          display={isMobile ? null : "flex"}
          alignItems="center"
          paddingBottom={[2, 4]}
        >
          <Heading as="h3" marginRight="3">
            <ResponsiveFormattedMessage
              mobile={messages.subscriptionFormula}
              desktop={messages.subscriptionFormulaShort}
              values={{
                plus: () => (
                  <Icon
                    as={BrandPlus}
                    boxSize="5"
                    marginLeft="1"
                    display="inline"
                  />
                ),
              }}
            />
          </Heading>
        </Box>
      }
      footer={
        <Box w="full">
          <Box mb="4">
            <PrettyRate
              cents={selectedPlan.priceCents}
              currency={selectedPlan.priceCurrency}
              unit={selectedPlan.period}
              moneyFontSize={{ base: "32px", xl: "40px" }}
            />
          </Box>
          <Button
            colorScheme="pink"
            size="lg"
            width="full"
            data-testid="choose-subscription-formula"
            onClick={handleContinue}
            background={`linear-gradient(96.51deg, ${orange500} 0%, ${pink500} 99.87%);`}
            _hover={{
              background: `linear-gradient(96.51deg, ${orange600} 0%, ${pink600} 99.87%);`,
            }}
          >
            <FormattedMessage {...messages.choose} />
          </Button>
          <Text fontSize="xs" textAlign="center" mt="2">
            <FormattedMessage
              {...messages.youCanCancelYourSubscriptionRenewalAtAnyMoment}
            />
          </Text>
        </Box>
      }
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        background={`linear-gradient(96.51deg, ${orange500} 0%, ${pink500} 99.87%);`}
        h="2"
        w="full"
      />
      <Box marginBottom="6">
        <Text marginBottom="4">
          <FormattedMessage {...messages.enjoyTheFullExperience} />
        </Text>
        <SubscriptionAdvantagesList />
        <Box my="4">
          <LabeledSwitch
            options={["quarterly_subscription", "yearly_subscription"]}
            getLabel={getSubscriptionLabel}
            onChange={handleSelectPlan}
            activeOption={selectedPlanName}
          />
        </Box>
      </Box>
    </Card>
  );
};
